import React from 'react';
import {
  Badge,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Tooltip,
} from '@mui/material';

export interface GroupedNavItem<T = number | string> {
  value: T;
  label: string;
  icon: React.ReactNode;
  badge?: number;
  badgeColor?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning';
  disabled?: boolean;
  disabledHint?: string;
  hide?: boolean;
}

export interface GroupedNavSection<T = number | string> {
  title: string;
  items: GroupedNavItem<T>[];
}

interface Props<T> {
  value: T;
  onChange: (next: T) => void;
  sections: GroupedNavSection<T>[];
  /** Optional: zusaetzlicher Inhalt unterhalb der Navigation (z. B. Aktions-Buttons). */
  footer?: React.ReactNode;
}

/**
 * Vertikale, gruppierte Sidebar-Navigation mit Icons + optionalen Badges.
 * Wird sowohl im Vorgang als auch im Mandanten verwendet — gleiche Optik,
 * verschiedene Sections.
 */
export function GroupedNavSidebar<T extends string | number>({
  value,
  onChange,
  sections,
  footer,
}: Props<T>) {
  return (
    <Box
      sx={{
        mt: 2,
        borderRight: theme => `1px solid ${theme.palette.divider}`,
        // Sticky-Sidebar: bleibt beim Scrollen des Dialog-Inhalts oben
        // sichtbar — verhindert eine zweite, nervige Scrollbar im Vorgang.
        // ``alignSelf: flex-start`` stellt sicher, dass das Grid-Item die
        // Hoehe der Sidebar nicht auf die Hoehe der Tab-Spalte streckt,
        // sonst greift sticky nicht.
        position: 'sticky',
        top: 0,
        alignSelf: 'flex-start',
        maxHeight: '90vh',
        overflowY: 'auto',
      }}
    >
      <List
        dense
        disablePadding
        sx={{ pb: 2, '& .MuiListSubheader-root': { lineHeight: '32px' } }}
      >
        {sections.map(section => {
          const visibleItems = section.items.filter(i => !i.hide);
          if (visibleItems.length === 0) return null;
          return (
            <React.Fragment key={section.title}>
              <ListSubheader
                disableSticky
                sx={{
                  bgcolor: 'transparent',
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  pl: 2,
                  pt: 1.5,
                }}
              >
                {section.title}
              </ListSubheader>
              {visibleItems.map(item => {
                const isActive = value === item.value;
                const button = (
                  <ListItemButton
                    key={String(item.value)}
                    selected={isActive}
                    disabled={item.disabled}
                    onClick={() => onChange(item.value)}
                    sx={{
                      pl: 2,
                      py: 0.6,
                      mx: 0.5,
                      mb: 0.3,
                      borderRadius: 1,
                      borderLeft: theme =>
                        isActive
                          ? `3px solid ${theme.palette.primary.main}`
                          : '3px solid transparent',
                      transition: 'background-color 0.15s, border-color 0.15s',
                      '&.Mui-selected': {
                        bgcolor: theme => `${theme.palette.primary.main}14`,
                      },
                      '&.Mui-selected:hover': {
                        bgcolor: theme => `${theme.palette.primary.main}1f`,
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 36,
                        color: isActive ? 'primary.main' : 'text.secondary',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: '0.9rem',
                        fontWeight: isActive ? 600 : 400,
                      }}
                    />
                    {item.badge !== undefined && item.badge > 0 && (
                      <Badge
                        badgeContent={item.badge}
                        color={item.badgeColor ?? 'primary'}
                        max={99}
                        sx={{
                          ml: 1,
                          mr: 1,
                          '& .MuiBadge-badge': {
                            position: 'static',
                            transform: 'none',
                          },
                        }}
                      />
                    )}
                  </ListItemButton>
                );
                if (item.disabled && item.disabledHint) {
                  return (
                    <Tooltip
                      key={String(item.value)}
                      title={item.disabledHint}
                      placement='right'
                    >
                      <Box>{button}</Box>
                    </Tooltip>
                  );
                }
                return button;
              })}
            </React.Fragment>
          );
        })}
      </List>
      {footer}
    </Box>
  );
}
